'use client';

import Image from 'next/image';
import { clsx } from 'clsx';
import { useQuery } from '@apollo/client/react';
import {
  SALES_REPRESENTATIVE_QUERY,
  type SalesRepresentative,
} from '@paeffgen/react/crm-documents/data-access-graphql';
import {
  resolveRepPhotoUrl,
  type BranchEmployeesData,
} from '@paeffgen/react/crm-documents/feature-dashboard';
import { useCurrentUser, useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { Skeleton } from '@paeffgen/react/crm-documents/ui';
import { getInitials } from './context-identity';
import { CustomerFilter } from './customer-filter';
// Statisches Filial-Team — Single Source of Truth für die Verkäufer-Fotos. Serverseitig
// in app/page.tsx via zod validiert; hier (Client) als getypter Build-Asset genutzt, um
// das Foto des Ansprechpartners per E-Mail anzureichern (NAV liefert keins).
import branchEmployeesJson from '../../content/branch-employees.json';

const branchEmployees = branchEmployeesJson as BranchEmployeesData;

interface CustomerContextPanelProps {
  /**
   * `'popover'` — kompaktes Desktop-Popover, `'sheet'` — Mobile-BottomSheet
   * (größere Touch-Targets). Steuert ausschließlich Dichte/Tap-Flächen,
   * nicht den Inhalt.
   */
  variant: 'popover' | 'sheet';
}

/**
 * Geteilter Kontext-Inhalt: Konto-Kopf, Cross-Login-Toggle, Debitor-Suche
 * und Ansprechpartner. Wird sowohl vom Desktop-`CustomerContextPopover` als
 * auch vom Mobile-`PillSheet` gerendert — Single Source of Truth statt der
 * bisherigen Duplizierung zwischen `app-shell` und `pill-sheet`.
 */
export function CustomerContextPanel({ variant }: CustomerContextPanelProps) {
  const { user, isLoading: userLoading } = useCurrentUser();
  const { isAdmin, allCustomers, setAllCustomers, selectedCustomers, adminQueryVars } =
    useAdmin();

  const isSheet = variant === 'sheet';

  const isSingle = selectedCustomers.length === 1;
  const isMulti = selectedCustomers.length >= 2;

  // Cross-Login Toggle — Farbe/Subtext folgen dem Banner-Modus:
  // einzelner Debitor → rot (Cross-Login), mehrere/alle → amber (Warnung).
  const toggleSubText = !allCustomers
    ? 'Aus — eigene Sicht'
    : selectedCustomers.length === 0
      ? 'An — alle Debitoren sichtbar'
      : isSingle
        ? 'An — 1 Debitor gewählt'
        : `An — ${selectedCustomers.length} Debitoren gewählt`;
  const toggleCardClass = !allCustomers
    ? 'bg-zinc-50 border-zinc-200'
    : isSingle
      ? 'bg-red-50 border-red-200'
      : 'bg-amber-50 border-amber-200';
  const switchBg = !allCustomers
    ? 'bg-zinc-300'
    : isSingle
      ? 'bg-red-500'
      : 'bg-amber-500';

  const { data: salesRepData, loading: salesRepLoading } = useQuery<{
    salesRepresentative: SalesRepresentative;
  }>(SALES_REPRESENTATIVE_QUERY, {
    variables: { ...adminQueryVars },
    fetchPolicy: 'cache-and-network',
  });
  const salesRep = salesRepData?.salesRepresentative;

  // Foto aus dem statischen Filial-Team anreichern (E-Mail-Abgleich); kein echtes
  // Foto → Initialen-Fallback unten.
  const repPhotoUrl = salesRep
    ? resolveRepPhotoUrl(branchEmployees, salesRep) ?? salesRep.photoUrl
    : null;

  // Ansprechpartner ausblenden, wenn mehrere Debitoren gewählt sind (kein
  // eindeutiger Ansprechpartner) oder noch keiner geladen wurde.
  const showSalesRep =
    !(allCustomers && selectedCustomers.length > 1) && !salesRepLoading && !!salesRep;

  return (
    <div className={clsx(isSheet ? 'space-y-4' : 'space-y-3')}>
      {/* Konto-Kopf */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[10px] font-extrabold text-zinc-400 uppercase tracking-widest">
            Kunde
          </p>
          {isAdmin && (
            <span className="text-[9px] font-bold uppercase tracking-wider bg-red-500 text-white px-1.5 py-0.5 rounded">
              Admin
            </span>
          )}
        </div>
        {userLoading ? (
          <>
            <Skeleton className="h-5 w-48 mb-1" />
            <Skeleton className="h-3 w-28 mt-1" />
          </>
        ) : (
          <>
            <p className="font-bold text-zinc-900 text-[15px] truncate">
              {user?.name ?? 'Unbekannt'}
            </p>
            {user?.email && user.email !== user.name && (
              <p className="text-xs text-zinc-500 mt-0.5 truncate">{user.email}</p>
            )}
            {user?.customerNumber && (
              <p className="text-xs text-zinc-500 font-mono mt-0.5">
                KD-Nr. {user.customerNumber}
              </p>
            )}
          </>
        )}
      </div>

      {/* Cross-Login Toggle (nur Admin) */}
      {isAdmin && (
        <section
          className={clsx(
            'border rounded-xl p-3 flex items-center gap-3 transition-colors',
            toggleCardClass,
          )}
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-zinc-900">Cross-Login</p>
            <p className="text-xs text-zinc-600 mt-0.5 truncate">{toggleSubText}</p>
          </div>
          <button
            type="button"
            onClick={() => setAllCustomers(!allCustomers)}
            aria-pressed={allCustomers}
            aria-label="Cross-Login umschalten"
            className={clsx('relative shrink-0 rounded-full transition-colors', switchBg)}
            style={{ width: 44, height: 24 }}
          >
            <span
              className="absolute bg-white rounded-full shadow transition-transform"
              style={{
                width: 20,
                height: 20,
                top: 2,
                left: 2,
                transform: allCustomers ? 'translateX(20px)' : 'translateX(0)',
              }}
            />
          </button>
        </section>
      )}

      {/* Debitor-Suche (nur Admin + Cross-Login aktiv) */}
      {isAdmin && allCustomers && <CustomerFilter size={isSheet ? 'md' : 'sm'} />}

      {/* Ansprechpartner */}
      {showSalesRep && salesRep && (
        <div className="-mx-1 px-3 py-3 bg-[antiquewhite] dark:bg-[#2a2418] rounded-xl">
          <p className="text-[10px] font-extrabold text-[#d2a373] dark:text-[#e0b483] uppercase tracking-widest mb-3">
            Ihr Ansprechpartner
          </p>
          <div className="flex items-center gap-3.5">
            {repPhotoUrl ? (
              <Image
                src={repPhotoUrl}
                alt={salesRep.name}
                width={44}
                height={44}
                className="w-11 h-11 rounded-full ring-2 ring-white shadow-sm object-cover bg-zinc-100"
              />
            ) : (
              <div className="w-11 h-11 rounded-full ring-2 ring-white shadow-sm bg-zinc-200 flex items-center justify-center text-zinc-500 text-sm font-bold">
                {getInitials(salesRep.name)}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-bold text-zinc-900 truncate">
                {salesRep.name}
              </p>
              <div className="flex flex-col gap-0.5 mt-1">
                <a
                  href={`tel:${salesRep.phone}`}
                  className="text-xs font-medium text-zinc-600 hover:text-primary transition-colors truncate"
                >
                  {salesRep.phone}
                </a>
                <a
                  href={`mailto:${salesRep.email}`}
                  className="text-xs font-medium text-zinc-600 hover:text-primary transition-colors truncate"
                >
                  {salesRep.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rechtliches — Pflichtangaben (§ 5 DDG). Verweist auf die bestehenden
          Seiten im Hauptshop und öffnet jeweils einen neuen Tab. */}
      <div
        className={clsx(
          'border-t border-zinc-100 flex items-center justify-center gap-3 text-xs text-zinc-400',
          isSheet ? 'pt-4' : 'pt-3',
        )}
      >
        <a
          href="https://www.dachbaustoffe.de/shop/rechte_info/impressum"
          target="_blank"
          rel="noopener noreferrer"
          className="py-1 hover:text-primary transition-colors"
        >
          Impressum
        </a>
        <span aria-hidden="true" className="text-zinc-300">
          ·
        </span>
        <a
          href="https://www.dachbaustoffe.de/shop/rechte_info/datenschutz"
          target="_blank"
          rel="noopener noreferrer"
          className="py-1 hover:text-primary transition-colors"
        >
          Datenschutz
        </a>
      </div>
    </div>
  );
}
